/**
  Note about Constants in relation to Typescript:

  Generally type-casting should be avoided, but in this file
  since everything should be static it's good practice to add "as const"
  as to tell Typescript that the content of this file never changes in runtime.
  This makes it possible for us to infer types from the values of these objects,
  reusing them across all types and keeping this file the single point of truth.

  example:

  const S = {
    key: value
  } as const


 */

import { MessageDescriptor } from "@lingui/core";
import { defineMessage, msg } from "@lingui/core/macro";
import {
  GlassesIcon,
  LucideIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  TrendingUpIcon,
  TriangleAlertIcon,
} from "lucide-react";

// If this is updated, sync with variable in settings/mentions.py
export const MENTIONS_VIEW_PAGE_SIZE = 40;

export const HARD_BREAK_MOBILE_PX = 768;

export const MODAL_ROOT_ID = "root-modal";

/**
 * Filter
 * Map for all the parameters that can be used in the URLS of the mention filters
 */
export const FILTER = {
  CHANNEL_NAME: "channel_name",
  INSIGHT_TYPES: "insight_types",
  INSIGHT_ID: "insight_id",
  TAG: "tag",
  CHANNEL_TYPE: "channel_type",
  FREETEXT: "freetext",
  LANGUAGE: "language",
  SENTIMENT: "sentiment",
  END_DATE: "end_date",
  START_DATE: "start_date",
  MIN_REACH: "min_reach_threshold",
  MENTION_KIND: "mention_kind",
  PAGE: "page",
  SORTING: "sort_by",
} as const;

export const SENTIMENT = {
  VERY_POSITIVE: "very_positive",
  POSITIVE: "positive",
  NEUTRAL: "neutral",
  NEGATIVE: "negative",
  VERY_NEGATIVE: "very_negative",
} as const;

export const SENTIMENT_TO_LABEL: Record<Sentiment, MessageDescriptor> = {
  [SENTIMENT.VERY_POSITIVE]: msg`Väldigt positivt`,
  [SENTIMENT.POSITIVE]: msg`Positivt`,
  [SENTIMENT.NEUTRAL]: msg`Neutralt`,
  [SENTIMENT.NEGATIVE]: msg`Negativt`,
  [SENTIMENT.VERY_NEGATIVE]: msg`Väldigt negativt`,
};

export const SENTIMENT_TO_COLOR: Record<Sentiment, string> = {
  [SENTIMENT.VERY_POSITIVE]: "var(--color-green-400)",
  [SENTIMENT.POSITIVE]: "var(--color-green-400)",
  [SENTIMENT.NEUTRAL]: "var(--color-neutral-200)",
  [SENTIMENT.NEGATIVE]: "var(--color-red-400)",
  [SENTIMENT.VERY_NEGATIVE]: "var(--color-red-400)",
};

export const USER_ACCESS = {
  READONLY: "readonly",
  USER: "user",
  ADMIN: "admin",
} as const;

export const USER_ACCESS_TYPES = Object.values(USER_ACCESS);

export const USER_ACCESS_TO_LABEL: Record<UserAccess, MessageDescriptor> = {
  [USER_ACCESS.READONLY]: msg`Begränsad användare`,
  [USER_ACCESS.USER]: defineMessage({
    message: "Användare",
    id: "singular-user",
  }),
  [USER_ACCESS.ADMIN]: msg`Admin`,
};

export const SEARCH_KIND = {
  REPORT: "report",
  SAVED: "static_report",
} as const;

export const INSTAGRAM_TITLE = {
  STORY: "InstagramStory",
  VIDEO: "InstagramVideo",
  IMAGE: "InstagramImage",
} as const;

export const SEARCH_TYPES = {
  BRAND: "brand",
  INDUSTRY_OR_AREA_OF_INTEREST: "industry_or_area_of_interest",
  COMPETITORS: "competitors",
  CAMPAIGN_OR_PROJECT: "campaign_or_project",
  KEY_PEOPLE: "key_people",
  OTHER: "other",
} as const;

export const SEARCH_TYPE_TO_LABEL: Record<SearchType, MessageDescriptor> = {
  [SEARCH_TYPES.BRAND]: msg`Eget varumärke, verksamhet eller företag`,
  [SEARCH_TYPES.INDUSTRY_OR_AREA_OF_INTEREST]: msg`Bransch / Intresseområde`,
  [SEARCH_TYPES.COMPETITORS]: msg`Konkurrenter`,
  [SEARCH_TYPES.CAMPAIGN_OR_PROJECT]: msg`Kampanjer / Projekt`,
  [SEARCH_TYPES.KEY_PEOPLE]: msg`Nyckelpersoner`,
  [SEARCH_TYPES.OTHER]: msg`Annat`,
};

export const MENTION_KIND = {
  AUDIO: "audio",
  TEXT: "text",
} as const;

export const MENTION_KIND_TO_LABEL: Record<MentionKind, MessageDescriptor> = {
  [MENTION_KIND.AUDIO]: msg`Ljud`,
  [MENTION_KIND.TEXT]: msg`Text`,
};

export const MENTION_TYPE = {
  EPISODE_NAME: "episode_name",
  EPISODE_DESCRIPTION: "episode_description",
  TEXT: "text",
  CHANNEL_TITLE: "channel_title",
  TEXT_POST: "text_post",
  PAYWALLED: "paywalled",
} as const;

export const SOURCE = {
  TIKTOK: "tiktok",
  INSTAGRAM: "instagram",
  PODCAST: "podcast",
  RADIO: "radio",
  YOUTUBE: "youtube",
  YOUTUBE_SHORTS: "youtube_shorts",
  TV: "tv",
  WEB_TV: "webtv",
  MANUAL: "manual",
  FACEBOOK: "facebook",
  TWITTER: "twitter",
  NEWS: "news",
  LINKEDIN: "linkedin",
} as const;

export const SOURCES = Object.values(SOURCE);

export const SOURCE_CODE_TO_LABEL: Record<Source, MessageDescriptor> = {
  [SOURCE.PODCAST]: msg`Podcast`,
  [SOURCE.RADIO]: msg`Radio`,
  [SOURCE.FACEBOOK]: msg`Facebook`,
  [SOURCE.INSTAGRAM]: msg`Instagram`,
  [SOURCE.LINKEDIN]: msg`LinkedIn`,
  [SOURCE.MANUAL]: msg`Manuell`,
  [SOURCE.NEWS]: msg`Nyhetsartiklar`,
  [SOURCE.TIKTOK]: msg`Tiktok`,
  [SOURCE.TV]: msg`TV`,
  [SOURCE.TWITTER]: msg`X`,
  [SOURCE.WEB_TV]: msg`Webb-TV`,
  [SOURCE.YOUTUBE]: msg`YouTube`,
  [SOURCE.YOUTUBE_SHORTS]: msg`YouTube Shorts`,
};

export const SOURCE_CODE_TO_SINGULAR_LABEL: Record<Source, MessageDescriptor> =
  {
    [SOURCE.PODCAST]: msg`Podcast`,
    [SOURCE.RADIO]: msg`Radio`,
    [SOURCE.FACEBOOK]: msg`Facebook`,
    [SOURCE.INSTAGRAM]: msg`Instagram`,
    [SOURCE.LINKEDIN]: msg`LinkedIn`,
    [SOURCE.MANUAL]: msg`Manuell`,
    [SOURCE.NEWS]: msg`Nyhetsartikel`,
    [SOURCE.TIKTOK]: msg`Tiktok`,
    [SOURCE.TV]: msg`TV`,
    [SOURCE.TWITTER]: msg`X`,
    [SOURCE.WEB_TV]: msg`Webb-TV`,
    [SOURCE.YOUTUBE]: msg`YouTube`,
    [SOURCE.YOUTUBE_SHORTS]: msg`YouTube Short`,
  };

export const TEXT: Source[] = [
  SOURCE.FACEBOOK,
  SOURCE.INSTAGRAM,
  SOURCE.TIKTOK,
  SOURCE.TWITTER,
  SOURCE.LINKEDIN,
  SOURCE.NEWS,
] as const;

export const AUDIO: Source[] = [
  SOURCE.PODCAST,
  SOURCE.RADIO,
  SOURCE.WEB_TV,
  SOURCE.TV,
  SOURCE.TIKTOK,
  SOURCE.YOUTUBE,
] as const;

export const TRANSCRIPTION_STATUS = {
  QUEUED: "queued",
  INDEXED: "indexed",
  FAILED: "failed",
} as const;

export const TRANSCRIPT_STATUS = {
  // Full list of available transcript statuses.
  // The same as TRANSCRIPT_STATUSES_ENUM in the backend.
  CREATED: "created",
  QUEUED_FOR_GEODOWNLOAD: "queued_for_geodownload",
  QUEUED_FOR_CONVERSION: "queued_for_conversion",
  QUEUED_FOR_TRANSCRIPTION: "queued_for_transcription",
  DOWNLOADING: "downloading",
  DOWNLOADED: "downloaded",
  CONVERTING: "converting",
  CONVERTED: "converted",
  ABORTED: "aborted",
  TRANSCRIBING: "transcribing",
  INDEXING: "indexing",
  INDEXED: "indexed",
  PARTLY_FAILED: "partly_failed",
  FAILED: "failed",
} as const;

export const TRANSCRIPT_SUCCESS_STATUSES = [TRANSCRIPT_STATUS.INDEXED] as const;

export const TRANSCRIPT_FAILED_STATUSES = [
  TRANSCRIPT_STATUS.FAILED,
  TRANSCRIPT_STATUS.PARTLY_FAILED,
  TRANSCRIPT_STATUS.ABORTED,
] as const;

export const TRANSCRIPT_IN_PROGRESS_STATUSES = [
  TRANSCRIPT_STATUS.CREATED,
  TRANSCRIPT_STATUS.QUEUED_FOR_GEODOWNLOAD,
  TRANSCRIPT_STATUS.QUEUED_FOR_CONVERSION,
  TRANSCRIPT_STATUS.QUEUED_FOR_TRANSCRIPTION,
  TRANSCRIPT_STATUS.DOWNLOADING,
  TRANSCRIPT_STATUS.DOWNLOADED,
  TRANSCRIPT_STATUS.CONVERTING,
  TRANSCRIPT_STATUS.CONVERTED,
  TRANSCRIPT_STATUS.TRANSCRIBING,
  TRANSCRIPT_STATUS.INDEXING,
] as const;

export const LOCALE = {
  SWEDISH: "sv",
  ENGLISH: "en",
} as const;

export const LOCALE_TO_LABEL: Record<Locale, MessageDescriptor> = {
  [LOCALE.SWEDISH]: msg`Svenska`,
  [LOCALE.ENGLISH]: msg`Engelska`,
};

export const LOCALES = Object.values(LOCALE);

export const LANGUAGE = {
  SWEDISH: "sv",
  ENGLISH: "en",
  DANISH: "da",
  GERMAN: "de",
  FINNISH: "fi",
  NORWEGIAN: "no",
  SPANISH: "es",
  FRENCH: "fr",
  ITALIAN: "it",
  DUTCH: "nl",
  PORTUGUESE: "pt",
} as const;

export const LANGUAGES = Object.values(LANGUAGE);

export const LANGUAGE_CODE_TO_LABEL: Record<Language, MessageDescriptor> = {
  [LANGUAGE.SWEDISH]: msg`Svenska`,
  [LANGUAGE.ENGLISH]: msg`Engelska`,
  [LANGUAGE.DANISH]: msg`Danska`,
  [LANGUAGE.DUTCH]: msg`Nederländska`,
  [LANGUAGE.FINNISH]: msg`Finska`,
  [LANGUAGE.FRENCH]: msg`Franska`,
  [LANGUAGE.GERMAN]: msg`Tyska`,
  [LANGUAGE.ITALIAN]: msg`Italienska`,
  [LANGUAGE.NORWEGIAN]: msg`Norska`,
  [LANGUAGE.PORTUGUESE]: msg`Portugisiska`,
  [LANGUAGE.SPANISH]: msg`Spanska`,
};

export const CURRENCY = {
  SEK: "SEK",
  USD: "USD",
  NOK: "NOK",
  EUR: "EUR",
} as const;

export const CURRENCIES = Object.values(CURRENCY);

export const CURRENCY_CODE_TO_LABEL: Record<Currency, MessageDescriptor> = {
  [CURRENCY.SEK]: msg`Svenska kronor`,
  [CURRENCY.USD]: msg`US Dollar`,
  [CURRENCY.EUR]: msg`Euro`,
  [CURRENCY.NOK]: msg`Norska kronor`,
};

export const SORTING_OPTION = {
  RELEVANCE: "relevance",
  DATE: "date",
  REACH: "reach",
} as const;

export const SEARCH_SUBSCRIPTION_INTERVAL = {
  REALTIME: "realtime",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  REALTIME_INSIGHTS_NEGATIVE: "realtime_insights_mail_negative_impact",
  REALTIME_INSIGHTS_POSITIVE: "realtime_insights_mail_positive_impact",
  REALTIME_INSIGHTS_HIGH_REACH: "realtime_insights_mail_high_reach",
} as const;

export const SEARCH_SUBSCRIPTION_INTERVAL_TO_LABEL = {
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME]: msg`Direkt`,
  [SEARCH_SUBSCRIPTION_INTERVAL.DAILY]: msg`Dagligen`,
  [SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY]: msg`Varje vecka`,
  [SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY]: msg`Varje månad`,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]: msg`Negativ påverkan`,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]: msg`Varumärkesbyggande`,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]: msg`Hög räckvidd`,
};

export const REPORT_SUBSCRIPTION_INTERVAL = {
  NONE: "none",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
} as const;

export const INTERVALS_FOR_TIME_COMPARISON = {
  [REPORT_SUBSCRIPTION_INTERVAL.DAILY]: msg`Dagsrapport`,
  [REPORT_SUBSCRIPTION_INTERVAL.WEEKLY]: msg`Veckorapport`,
  [REPORT_SUBSCRIPTION_INTERVAL.MONTHLY]: msg`Månadsrapport`,
  [REPORT_SUBSCRIPTION_INTERVAL.QUARTERLY]: msg`Kvartalsrapport`,
  [REPORT_SUBSCRIPTION_INTERVAL.YEARLY]: msg`Årsrapport`,
};

export const REPORT_SUBSCRIPTION_INTERVAL_TO_LABEL = {
  [REPORT_SUBSCRIPTION_INTERVAL.NONE]: msg`Aldrig`,
  [REPORT_SUBSCRIPTION_INTERVAL.DAILY]: msg`Varje dag`,
  [REPORT_SUBSCRIPTION_INTERVAL.WEEKLY]: msg`Varje vecka`,
  [REPORT_SUBSCRIPTION_INTERVAL.MONTHLY]: msg`Varje månad`,
  [REPORT_SUBSCRIPTION_INTERVAL.QUARTERLY]: msg`Varje kvartal`,
  [REPORT_SUBSCRIPTION_INTERVAL.YEARLY]: msg`Varje år`,
};

export const REPORT_TEMPLATE_TYPE = {
  OVERVIEW: "overview",
  COMPARISON: "comparison",
  COMPARISON_WITH_REACH: "comparison_with_reach",
  TIME_COMPARISON: "time_comparison",
} as const;

export const REPORT_TEMPLATE_TO_LABEL = {
  [REPORT_TEMPLATE_TYPE.OVERVIEW]: msg`Översikt`,
  [REPORT_TEMPLATE_TYPE.COMPARISON]: msg`Jämförelse`,
  [REPORT_TEMPLATE_TYPE.COMPARISON_WITH_REACH]: msg`Jämförelse`,
  [REPORT_TEMPLATE_TYPE.TIME_COMPARISON]: msg`Tidsjämförelse`,
};

export const METRIC = {
  NUM_MENTIONS: "num_mentions",
  REACH: "reach",
  PR_VALUE: "pr_value",
  SENTIMENT: "sentiment",
  TOP_CHANNELS: "top_channels",
  MEDIA_TYPES: "media_types",
  MENTIONS: "mentions",
  ENGAGEMENT: "engagement",
  POPULARITY_SCORE: "popularity_score",
  WORD_CLOUD: "word_cloud",
  INSIGHTS_TOP_CHANNELS: "insights_top_channels",
  INSIGHTS_TOP_MENTIONS: "insights_top_mentions",
  INSIGHTS_MENTIONS_REACH: "insights_mentions_reach",
  INSIGHTS_NUM_MENTIONS_OVER_TIME: "insights_num_mentions_over_time",
  INSIGHTS_REACH_OVER_TIME: "insights_reach_over_time",
  INSIGHTS_REACH_OVER_TIME_AUDIO: "insights_reach_over_time_audio",
  INSIGHTS_REACH_OVER_TIME_SOCIAL: "insights_reach_over_time_social",
  INSIGHTS_PR_VALUE_OVER_TIME: "insights_pr_value_over_time",
  INSIGHTS_NUM_CHANNELS_OVER_TIME: "insights_num_channels_over_time",
  INSIGHTS_SENTIMENT_OVER_TIME_PER_MEDIA_CATEGORY:
    "insights_sentiment_over_time_per_media_category",
  INSIGHTS_NUM_MENTIONS_PER_MEDIA_CATEGORY:
    "insights_num_mentions_per_media_category",
  INSIGHTS_SENTIMENT: "insights_sentiment",
  INSIGHTS_WORD_CLOUD: "insights_word_cloud",
} as const;

export const TASK_STATUS = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  RETRY: "RETRY",
  FAILURE: "FAILURE",
};
export const MARKET = {
  US: "us",
  SWEDEN: "se",
  DENMARK: "dk",
  NORWAY: "no",
} as const;

export const MENTION_SOURCE_TYPE = {
  SPEECH: "speech",
  TEXT: "text",
  ALL: "all",
} as const;

export const MENTION_SOURCE_TYPES = Object.values(MENTION_SOURCE_TYPE);

export const MENTION_SOURCE_TYPE_TO_LABEL = {
  [MENTION_SOURCE_TYPE.SPEECH]: msg`Ljud`,
  [MENTION_SOURCE_TYPE.TEXT]: msg`Text`,
  [MENTION_SOURCE_TYPE.ALL]: msg`Allt`,
};

export const DELIMITERS = [".", ",", "!", "?", "…"];

export const MAXIMUM_CLUSTER_DISTANCE_SECONDS = 120;
export const BACKEND_MENTION_TIMING_ADJUSTMENT = 3; // seconds

// The last word in transcripts does not have a set ending. Let's add some safe space so that we're not cutting to early.
export const LAST_WORD_PADDING_SECONDS = 0.5;

// Fallbacks for internationalization
export const FALLBACK_CURRENCY: Currency = "SEK";
export const FALLBACK_LANGUAGE: Locale = "sv";

export const NON_AUDIO_MEDIA: Partial<Source>[] = [
  SOURCE.FACEBOOK,
  SOURCE.TWITTER,
  SOURCE.NEWS,
  SOURCE.LINKEDIN,
];
export const ANALYSIS_DATA_TYPE = {
  ALL: "alla", // This is a meta type for aggregated data generated locally.
  SPOKEN: "talade",
  SOCIAL: "sociala",
  NEWS: "redakationella",
  INDUSTRY_INDEX: "branschindex",
} as const;

export const DATE_FORMAT = {
  API: "yyyy-MM-dd",
  LONG: "PPP",
  LONG_WITH_TIME: "HH:mm, PPP",
} as const;

export const INSIGHT_TYPE = {
  MOST_MENTIONS_FOR_CHANNEL_TYPE: "most_mentions_for_channel_type",
  MOST_MENTIONS: "most_mentions",
  MORE_MENTIONS_THAN_USUAL: "more_mentions_than_usual",
  HIGH_REACH_MENTION: "high_reach_mention",
  TOPLIST_MENTIONS: "toplist_mentions",
  MORE_CHANNELS_THAN_USUAL: "more_channels_than_usual",
  NEGATIVE_IMPACT: "negative_impact",
  POSITIVE_IMPACT: "positive_impact",
} as const;

export const INSIGHT_TYPE_TO_LABEL = {
  [INSIGHT_TYPE.MOST_MENTIONS_FOR_CHANNEL_TYPE]: msg`Nytt rekord`,
  [INSIGHT_TYPE.MOST_MENTIONS]: msg`Nytt rekord`,
  [INSIGHT_TYPE.MORE_MENTIONS_THAN_USUAL]: msg`Fler omnämnanden än vanligt`,
  [INSIGHT_TYPE.HIGH_REACH_MENTION]: msg`Hög räckvidd`,
  [INSIGHT_TYPE.TOPLIST_MENTIONS]: msg`100 på Apple iTunes`,
  [INSIGHT_TYPE.MORE_CHANNELS_THAN_USUAL]: msg`Flera omnämnanden i unika källor`,
  [INSIGHT_TYPE.NEGATIVE_IMPACT]: msg`Negativ påverkan`,
  [INSIGHT_TYPE.POSITIVE_IMPACT]: msg`Varumärkesbyggande`,
};

export const PLAN_TYPE = {
  FREE: "free",
  BASIC: "basic",
  CUSTOM: "custom",
  US_FREE: "us_free",
  US_BASIC: "us_basic",
} as const;

export const USE_CASE = {
  BRAND: "brand",
  TRENDS: "trends",
  FEEDBACK: "feedback",
  CRISIS: "crisis",
  COMPETITORS: "competitors",
  OTHER: "other",
} as const;

export const PLAN_TYPE_TO_LABEL = {
  [PLAN_TYPE.FREE]: msg`Free`,
  [PLAN_TYPE.BASIC]: msg`Basic`,
  [PLAN_TYPE.CUSTOM]: msg`Enterprise`,
  [PLAN_TYPE.US_FREE]: msg`Free`,
  [PLAN_TYPE.US_BASIC]: msg`Basic`,
};

// Export types for the constants stated above
export type Source = (typeof SOURCE)[keyof typeof SOURCE];
export type SearchStatus =
  | "active"
  | "idle"
  | "resetting"
  | "initializing"
  | "archived"
  | "searching";
export type Locale = (typeof LOCALE)[keyof typeof LOCALE];
export type SortingOption =
  (typeof SORTING_OPTION)[keyof typeof SORTING_OPTION];
export type SortingOptionV4 =
  | "created"
  | "published_date"
  | "reach"
  | "relevance"
  | "popularity";
export type MentionType = (typeof MENTION_TYPE)[keyof typeof MENTION_TYPE];
export type MentionKind = (typeof MENTION_KIND)[keyof typeof MENTION_KIND];
export type Sentiment = (typeof SENTIMENT)[keyof typeof SENTIMENT];
export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY];
export type Metric = (typeof METRIC)[keyof typeof METRIC];
export type ChartType =
  | "line"
  | "sum"
  | "bar"
  | "area_stacked"
  | "table"
  | "pie";
export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];
export type SearchSubscriptionInterval =
  (typeof SEARCH_SUBSCRIPTION_INTERVAL)[keyof typeof SEARCH_SUBSCRIPTION_INTERVAL];
export type ReportSubscriptionInterval =
  (typeof REPORT_SUBSCRIPTION_INTERVAL)[keyof typeof REPORT_SUBSCRIPTION_INTERVAL];
export type ReportTemplateType =
  (typeof REPORT_TEMPLATE_TYPE)[keyof typeof REPORT_TEMPLATE_TYPE];
export type Language = (typeof LANGUAGE)[keyof typeof LANGUAGE];
export type Market = (typeof MARKET)[keyof typeof MARKET];
export type MentionSourceType =
  (typeof MENTION_SOURCE_TYPE)[keyof typeof MENTION_SOURCE_TYPE];
export type InstagramTitle =
  (typeof INSTAGRAM_TITLE)[keyof typeof INSTAGRAM_TITLE];
export type UserAccess = (typeof USER_ACCESS)[keyof typeof USER_ACCESS];
export type PopularityScoreTrend = "new" | "falling" | "rising" | "unchanged";
export type TranscriptionStatus =
  (typeof TRANSCRIPTION_STATUS)[keyof typeof TRANSCRIPTION_STATUS];
export type TranscriptStatus =
  (typeof TRANSCRIPT_STATUS)[keyof typeof TRANSCRIPT_STATUS];
export type SearchType = (typeof SEARCH_TYPES)[keyof typeof SEARCH_TYPES];
export type TimePeriod = "day" | "week" | "month" | "year";
export type SearchKind = (typeof SEARCH_KIND)[keyof typeof SEARCH_KIND];
export type InsightType = (typeof INSIGHT_TYPE)[keyof typeof INSIGHT_TYPE];
export type UiTheme = "default" | "dark" | "system";
export type PlanType = (typeof PLAN_TYPE)[keyof typeof PLAN_TYPE];
export type UseCase = (typeof USE_CASE)[keyof typeof USE_CASE];

// Filter keys for widgets
export const WIDGET_FILTER = {
  MEDIA_TYPE: "media_type", // specific channel types
  MENTION_MEDIA_CATEGORY: "mention_media_category", // channel categories
  SENTIMENT: "sentiment",
};
export type AnalysisDataType =
  (typeof ANALYSIS_DATA_TYPE)[keyof typeof ANALYSIS_DATA_TYPE];

export const API_TOKEN_ERROR = {
  NO_TOKEN_FOUND: "No token found for this user.",
} as const;

export const TOS_LINKS = {
  YOUTUBE: "https://www.youtube.com/static?template=terms",
  GOOGLE: "https://policies.google.com/privacy?hl=en-US",
};

export const AE_USE_CASES: {
  id: UseCase;
  title: MessageDescriptor;
  description: MessageDescriptor;
  icon: LucideIcon;
}[] = [
  {
    id: USE_CASE.BRAND,
    title: msg`Förstå varumärke eller verksamhet`,
    description: msg`Ta del av samtalet och se hur ni omnämns`,
    icon: MegaphoneIcon,
  },
  {
    id: USE_CASE.TRENDS,
    title: msg`Håll koll på trender och ämnen`,
    description: msg`Följ branschsnacket i realtid`,
    icon: TrendingUpIcon,
  },
  {
    id: USE_CASE.FEEDBACK,
    title: msg`Få koll på vad kunderna tycker`,
    description: msg`Se vad som sägs om era produkter`,
    icon: MessagesSquareIcon,
  },
  {
    id: USE_CASE.CRISIS,
    title: msg`Krishantering`,
    description: msg`Fånga upp negativa omnämnanden i tid`,
    icon: TriangleAlertIcon,
  },
  {
    id: USE_CASE.COMPETITORS,
    title: msg`Håll ett öga på konkurrenterna`,
    description: msg`Se hur de omnämns och tas emot`,
    icon: GlassesIcon,
  },
];

export const CLOUDFLARE_TURNSTILE_SITE_KEY = import.meta.env
  .VITE_CLOUDFLARE_TURNSTILE_SITE_KEY;

type PricingPlan = {
  planType: PlanType;
  title: MessageDescriptor;
  description: MessageDescriptor;
  price:
    | {
        value: Record<Currency, number>;
        billing: MessageDescriptor;
      }
    | MessageDescriptor;
  features: {
    label: MessageDescriptor;
    info: MessageDescriptor;
  }[];
};

const US_BASIC_PRICING_PLAN: PricingPlan = {
  planType: PLAN_TYPE.US_BASIC,
  title: msg`Team`,
  description: msg`Bäst för mindre, snabbrörliga team som vill dra nytta av fördelarna med social intelligens`,
  price: {
    value: {
      [CURRENCY.SEK]: 9995,
      [CURRENCY.NOK]: 9995,
      [CURRENCY.USD]: 995,
      [CURRENCY.EUR]: 995,
    },
    billing: msg`/mån, betalas årsvis`,
  },
  features: [
    {
      label: msg`5 bevakningar, obegränsat resultat`,
      info: msg`Upp till 5 bevakningar ingår. Täckning och resultat beror på din konfiguration, sök i historisk data.`,
    },
    {
      label: msg`TikTok, YouTube och Podcasts`,
      info: msg`Inkluderar kärnkällor som TikTok, YouTube och poddar. Tillgänglighet varierar per marknad – sociala medier, TV och radio kan läggas till.`,
    },
    {
      label: msg`1 språk`,
      info: msg`Ett språk ingår som standard. Flera språk stöds, inklusive engelska, svenska, tyska, franska, spanska och flera.`,
    },
    {
      label: msg`Månadsrapporter`,
      info: msg`Skapa och jämför rapporter över tid. Anpassade rapporter via en expert finns tillgängliga på Enterprise.`,
    },
  ],
};

const US_CUSTOM_PRICING_PLAN = {
  planType: PLAN_TYPE.CUSTOM,
  title: msg`Enterprise`,
  description: msg`Byggd för skala, anpassade lösningar, dedikerad infrastruktur och strategisk AI-vägledning`,
  price: msg`Prata med oss`,
  features: [
    {
      label: msg`Oändligt antal bevakningar & resultat`,
      info: msg`Skapa obegränsat antal bevakningar och sök i historisk data.`,
    },
    {
      label: msg`Upp till 12 språk`,
      info: msg`Stöd för alla tillgängliga språk, inklusive engelska, svenska, tyska, franska, spanska, italienska, portugisiska, nederländska, danska, norska och finska.`,
    },
    {
      label: msg`API & RSS Integration`,
      info: msg`Koppla till Slack, API:er och RSS. Fler möjligheter finns på Enterprise.`,
    },
    {
      label: msg`Skräddarsydda rapporter`,
      info: msg`Skapa och jämför rapporter över tid. Anpassade rapporter via en expert finns tillgängliga på Enterprise.`,
    },
    {
      label: msg`Dedikerad kundsupport`,
      info: msg`Chattsupport ingår. Enterprise ger dig en dedikerad kontakt för uppsättning och vägledning.`,
    },
  ],
};

const SE_BASIC_PRICING_PLAN: PricingPlan = {
  planType: PLAN_TYPE.BASIC,
  title: msg`Team`,
  description: msg`Bäst för mindre, snabbrörliga team som vill dra nytta av fördelarna med social intelligens`,
  price: {
    value: {
      [CURRENCY.SEK]: 6995,
      [CURRENCY.NOK]: 6995,
      [CURRENCY.USD]: 699,
      [CURRENCY.EUR]: 699,
    },
    billing: msg`/mån, betalas årsvis`,
  },
  features: [
    {
      label: msg`5 bevakningar, obegränsat resultat`,
      info: msg`Upp till 5 bevakningar ingår. Täckning och resultat beror på din konfiguration, sök i historisk data.`,
    },
    {
      label: msg`TikTok, YouTube och Podcasts`,
      info: msg`Inkluderar kärnkällor som TikTok, YouTube och poddar. Tillgänglighet varierar per marknad – sociala medier, TV och radio kan läggas till.`,
    },
    {
      label: msg`1 språk`,
      info: msg`Ett språk ingår som standard. Flera språk stöds, inklusive engelska, svenska, tyska, franska och spanska.`,
    },
    {
      label: msg`Månadsrapporter`,
      info: msg`Skapa och jämför rapporter över tid. Anpassade rapporter via en expert finns tillgängliga på Enterprise.`,
    },
  ],
};

const SE_CUSTOM_PRICING_PLAN = {
  planType: PLAN_TYPE.CUSTOM,
  title: msg`Enterprise`,
  description: msg`Byggd för skala, anpassade lösningar, dedikerad infrastruktur och strategisk AI-vägledning`,
  price: msg`Anpassat pris`,
  features: [
    {
      label: msg`Oändligt antal bevakningar & resultat`,
      info: msg`Skapa obegränsat antal bevakningar och sök i historisk data.`,
    },
    {
      label: msg`Upp till 12 språk`,
      info: msg`Stöd för alla tillgängliga språk, inklusive engelska, svenska, tyska, franska, spanska, italienska, portugisiska, nederländska, danska, norska och finska.`,
    },
    {
      label: msg`API & RSS Integration`,
      info: msg`Koppla till Slack, API:er och RSS. Fler möjligheter finns på Enterprise.`,
    },
    {
      label: msg`Skräddarsydda rapporter`,
      info: msg`Skapa och jämför rapporter över tid. Anpassade rapporter via en expert finns tillgängliga på Enterprise.`,
    },
    {
      label: msg`Dedikerad kundsupport`,
      info: msg`Chattsupport ingår. Enterprise ger dig en dedikerad kontakt för uppsättning och vägledning.`,
    },
  ],
};

// The key is the country the org has, and the value is the plans they can upgrade to
export const PRICING_PLANS: Record<Market, PricingPlan[]> = {
  [MARKET.US]: [US_BASIC_PRICING_PLAN, US_CUSTOM_PRICING_PLAN],
  [MARKET.SWEDEN]: [SE_BASIC_PRICING_PLAN, SE_CUSTOM_PRICING_PLAN],
  [MARKET.NORWAY]: [],
  [MARKET.DENMARK]: [],
};

export const SE_EXTERNAL_PRICING_URL = "https://www.allears.ai/sv/pris";
export const EN_EXTERNAL_PRICING_URL = "https://www.allears.ai/en/pricing";
export const SE_EXTERNAL_URL = "https://www.allears.ai/sv";
export const EN_EXTERNAL_URL = "https://www.allears.ai/en";

export const SEARCH_TYPES_LIST = [
  SEARCH_TYPES.BRAND,
  SEARCH_TYPES.INDUSTRY_OR_AREA_OF_INTEREST,
  SEARCH_TYPES.COMPETITORS,
  SEARCH_TYPES.CAMPAIGN_OR_PROJECT,
  SEARCH_TYPES.KEY_PEOPLE,
  SEARCH_TYPES.OTHER,
] as const;

export const MCP_TIME_SERIES_GRANULARITY = {
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
} as const;

export type MCPTimeSeriesGranularity =
  (typeof MCP_TIME_SERIES_GRANULARITY)[keyof typeof MCP_TIME_SERIES_GRANULARITY];
